import { useEffect, useMemo, useRef, useState } from "react";

export type SearchableSelectOption = {
  value: number;
  label: string;
  hint?: string;
};

type Props = {
  id?: string;
  options: SearchableSelectOption[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
};

export function SearchableSelect({
  id,
  options,
  value,
  onChange,
  placeholder = "Search…",
  emptyMessage = "No matches",
  disabled = false,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((option) => option.value === value);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.hint?.toLowerCase().includes(needle),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  function selectOption(next: number) {
    onChange(next);
    setQuery("");
    setOpen(false);
  }

  function clearSelection() {
    onChange(null);
    setQuery("");
    setOpen(false);
  }

  return (
    <div className={`searchable-select${open ? " is-open" : ""}`} ref={rootRef}>
      <input
        id={id}
        type="search"
        className="searchable-select-input"
        value={open ? query : (selected?.label ?? "")}
        placeholder={selected ? selected.label : placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          if (selected) setQuery("");
        }}
      />
      {open && !disabled && (
        <ul className="searchable-select-list" role="listbox">
          {value != null && (
            <li>
              <button type="button" className="searchable-select-option searchable-select-clear" onClick={clearSelection}>
                Clear selection
              </button>
            </li>
          )}
          {filtered.length === 0 ? (
            <li className="searchable-select-empty">{emptyMessage}</li>
          ) : (
            filtered.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  className={`searchable-select-option${option.value === value ? " is-selected" : ""}`}
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => selectOption(option.value)}
                >
                  <span className="searchable-select-label">{option.label}</span>
                  {option.hint && <span className="searchable-select-hint">{option.hint}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
